import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Api } from '../../services/api';

const FALLBACK_STATUSES = ['Pending', 'Confirmed', 'Shipped', 'Delivering', 'Completed'];

const resolveImageFromList = (images) => {
  if (!Array.isArray(images)) return '';
  for (const entry of images) {
    if (!entry) continue;
    if (typeof entry === 'string' && entry) {
      return entry;
    }
    const candidate =
      entry.imageUrl ||
      entry.url ||
      entry.image ||
      entry.thumbnail ||
      null;
    if (candidate) {
      return candidate;
    }
  }
  return '';
};

const pickFirstImage = (source) => {
  if (!source) return '';
  const fromList = resolveImageFromList(source.images);
  if (fromList) return fromList;
  return (
    source.imageUrl ||
    source.image ||
    source.thumbnail ||
    source.cover ||
    ''
  );
};

const getStoredOrder = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem('selectedOrderData');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const sanitizeNumber = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const OrderDetailsPage = () => {
  const [orderRaw, setOrderRaw] = useState(getStoredOrder);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusList, setStatusList] = useState([]);

  const orderId =
    (typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('id')) ||
    orderRaw?.orderId ||
    orderRaw?.id;

  const loadOrder = useCallback(
    async (force = false) => {
      if (!orderId) return;
      if (!force && orderRaw && (orderRaw.orderId || orderRaw.id)) return;
      try {
        if (!orderRaw) {
          setLoading(true);
        }
        const response = await Api.fetchOrderById(orderId);
        const payload =
          response?.data?.data || response?.data || response || null;
        if (payload) {
          setOrderRaw(payload);
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(
              'selectedOrderData',
              JSON.stringify(payload)
            );
          }
        }
      } catch (err) {
        setError(err?.message || 'Unable to load order details.');
      } finally {
        setLoading(false);
      }
    },
    [orderId, orderRaw]
  );

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  useEffect(() => {
    let cancelled = false;
    const fetchStatuses = async () => {
      try {
        const response = await Api.fetchOrderStatuses();
        if (cancelled) return;
        const payload = response?.data?.data || response?.data || [];
        setStatusList(Array.isArray(payload) ? payload : []);
      } catch {
        setStatusList([]);
      }
    };
    fetchStatuses();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!orderId) return undefined;
    const id = setInterval(() => loadOrder(true), 15000);
    return () => clearInterval(id);
  }, [loadOrder, orderId]);

  const statuses = useMemo(() => {
    const backend = (Array.isArray(statusList) ? statusList : [])
      .filter((status) => (status?.name || '').toLowerCase() !== 'cancelled')
      .map((status, index) => {
        const name = (status?.name || '').toString().trim();
        return {
          id: status.orderStatus_id || status.orderStatusId || index,
          name,
          slug: name.toLowerCase(),
        };
      });
    if (backend.length) return backend;
    return FALLBACK_STATUSES.map((name, index) => ({
      id: index,
      name,
      slug: name.toLowerCase(),
    }));
  }, [statusList]);

  const order = useMemo(() => {
    if (!orderRaw) return null;
    const carts = Array.isArray(orderRaw.carts) ? orderRaw.carts : [];
    const customItems = Array.isArray(orderRaw.items) ? orderRaw.items : [];
    const items = carts.length
      ? carts.map((cart) => {
          const product = cart.product || {};
          const variant = cart.variant || {};
          const qty = sanitizeNumber(cart.quantity) || 1;
          const unit =
            typeof cart.unitPrice === 'number'
              ? cart.unitPrice
              : sanitizeNumber(cart.price_unit);
          const variantImage = pickFirstImage(variant);
          const productImage = pickFirstImage(product);
          return {
            id: cart.cartId || `${product.productId}:${variant.variantId}`,
            name: product.name || cart.name || 'Item',
            image: variantImage || productImage || cart.image || '',
            color: variant.color?.name || cart.color || '',
            quantity: qty,
            price: unit,
          };
        })
      : customItems.map((item) => ({
          id: item.id || item.name,
          name: item.name || 'Item',
          image: item.image || '',
          color: item.color || '',
          quantity: sanitizeNumber(item.quantity) || 1,
          price: sanitizeNumber(item.price),
        }));

    const itemsCount = items.reduce(
      (sum, item) => sum + Number(item.quantity || 1),
      0
    );
    const subtotal = items.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
      0
    );

    const cartsShipping = carts.reduce((sum, cart) => {
      const perUnit = sanitizeNumber(cart?.product?.shipping);
      const qty = sanitizeNumber(cart.quantity) || 1;
      return sum + perUnit * qty;
    }, 0);
    const itemsShipping = customItems.reduce(
      (sum, item) =>
        sum + sanitizeNumber(item?.shipping) + sanitizeNumber(item?.service),
      0
    );
    const shipping = cartsShipping + itemsShipping;

    const promo = orderRaw.promoCode || orderRaw.promo || null;
    const promoAmount = promo ? sanitizeNumber(promo.amount) : 0;
    const promoIsCashback = promo ? Boolean(promo.cashback) : false;
    const discountValue = promo
      ? promoIsCashback
        ? promoAmount
        : (subtotal * promoAmount) / 100
      : 0;

    const total =
      Number(orderRaw.totalAmount) > 0
        ? Number(orderRaw.totalAmount)
        : subtotal + shipping - discountValue;

    const addressParts = [
      orderRaw.address?.street ||
        orderRaw.address?.location_description ||
        orderRaw.address?.locationDescription ||
        orderRaw.shippingAddress ||
        '',
      orderRaw.address?.city,
      orderRaw.address?.country,
    ].filter(Boolean);

    const currentStatus =
      orderRaw.orderStatus?.name ||
      orderRaw.status ||
      (orderRaw.completed ? 'Completed' : 'Pending');
    const statusSlug = (currentStatus || '').toLowerCase();
    let stepIndex = statuses.findIndex(
      (status) => status.slug === statusSlug
    );
    if (stepIndex < 0) {
      stepIndex = statuses.findIndex((status) =>
        statusSlug.includes(status.slug)
      );
    }
    if (stepIndex < 0) stepIndex = 0;

    return {
      id: orderRaw.orderNumber || orderRaw.orderId || orderId || '—',
      date: orderRaw.date || orderRaw.dateCreated || '—',
      tracking: orderRaw.barcode || orderRaw.trackingNumber || '—',
      itemsCount,
      address: addressParts.length ? addressParts.join(', ') : '—',
      paymentMethod:
        orderRaw.paymentMethod ||
        orderRaw.payment ||
        'Cash on Delivery',
      status: currentStatus || 'Pending',
      stepIndex,
      items,
      subtotal,
      shipping,
      discountValue,
      promoLabel: promo
        ? promoIsCashback
          ? `-$${promoAmount.toFixed(2)}`
          : `-${promoAmount}%`
        : null,
      total,
    };
  }, [orderRaw, statuses, orderId]);

  if (loading && !order) {
    return (
      <div className="order-details">
        <p>Loading order details…</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details">
        <p>{error || 'We could not find that order.'}</p>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => window.location.assign('/orders')}
        >
          Back to orders
        </button>
      </div>
    );
  }

  return (
    <div className="order-details">
      <header className="order-details__header">
        <h2>Order Details</h2>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => window.location.assign('/orders')}
        >
          Back to orders
        </button>
      </header>

      <div className="order-progress">
        {statuses.map((status, index) => (
          <Fragment key={status.id || status.name}>
            <div className="order-progress__step">
              <span
                className={`order-progress__dot ${
                  index <= order.stepIndex ? 'is-active' : ''
                }`}
              />
              <p>{status.name}</p>
            </div>
            {index < statuses.length - 1 && (
              <span
                className={`order-progress__connector ${
                  index < order.stepIndex ? 'is-active' : ''
                }`}
              />
            )}
          </Fragment>
        ))}
      </div>
      <label>Order Information</label>
      <section className="order-panel">
        <header>
          <div>
            <p className='order-titles'>Date: <span className='order-subtitle'>{order.date}</span></p>
            <p className='order-titles'>Tracking number: <span className='order-subtitle'>{order.tracking}</span></p>
            <p className='order-titles'>Items number: <span className='order-subtitle'>{order.itemsCount}</span></p>
            <p className='order-titles'>Shipping Address: <span className='order-subtitle'>{order.address}</span></p>
            <p className='order-titles'>Payment method: <span className='order-subtitle'>{order.paymentMethod}</span></p>
          </div>
          <span className="order-panel__status">{order.status}</span>
        </header>
      </section>

      <label>Your Order</label>
      <section className="order-panel">
       
        <div className="order-items">
          {order.items.map((item) => (
            <article className="order-item" key={item.id}>
              <div className="order-item__media">
                {item.image ? (
                  <img src={item.image} alt={item.name} />
                ) : (
                  <span>No image</span>
                )}
              </div>
              <div className="order-item__body">
                <p className="order-item__name">{item.name}</p>
                <p className="order-item__meta">
                  {item.color ? `Color: ${item.color} · ` : ''}
                  Quantity: {item.quantity}
                </p>
              </div>
              <div className="order-item__price">
                {formatCurrency(item.price)}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="order-panel order-panel--summary">
        <div>
          <span>Items</span>
          <strong>{order.itemsCount}</strong>
        </div>
        <div>
          <span>Subtotal</span>
          <strong>{formatCurrency(order.subtotal)}</strong>
        </div>
        {order.discountValue > 0 && (
          <div>
            <span>Promo</span>
            <strong>{order.promoLabel}</strong>
          </div>
        )}
        <div>
          <span>Shipping</span>
          <strong>
            {order.shipping > 0 ? formatCurrency(order.shipping) : 'Free'}
          </strong>
        </div>
        <div className="order-panel__total">
          <span>Total Payment</span>
          <strong>{formatCurrency(order.total)}</strong>
        </div>
      </section>
    </div>
  );
};

export default OrderDetailsPage;
